import React, { useMemo } from 'react';
import { uniqueId } from 'lodash';
import { max } from 'd3-array';

import { withParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AreaClosed } from '@visx/shape';
import { curveMonotoneX as curve } from '@visx/curve';

import { scaleColorTraffic as scaleColor } from 'js/utils/color';
import { isScoredType } from 'js/utils/question-set';
import {
  childContributionFractions,
  createOrgAssessmentWeightContext,
  orgPathRowsForChildWeights,
  questionSetContributionFractions,
} from 'js/utils/org-assessment-rollup';
import { weightedHistogramBins } from './distributionBins';

const scoreHelpers = {
  question: {
    getScore: (childNode, questionSetKey, questionKey) => {
      const question =
        childNode.data?.scores?.[questionSetKey]?.questions?.[questionKey];
      if (!question || question.score == null) {
        return null;
      }
      if (!isScoredType(question.question_type)) {
        return null;
      }

      return {
        score: question.score,
        question_type: question.question_type,
      };
    },
  },
};

const RecommendationResultsDistribution = ({
  parentWidth,
  height = 20,
  node,
  organization,
  questionKey,
  questionSetKey,
  binCount = 11,
  margin = {
    top: 0.5,
    left: 0.5,
    bottom: 0.5,
    right: 0.5,
  },
}) => {
  const width = parentWidth;
  const scaleHeight = height - margin.top - margin.bottom;
  const scaleWidth = width - margin.left - margin.right;
  const { maskId, gradientId } = useMemo(() => {
    const chartId = uniqueId('recommendation-results-distribution-');
    return {
      chartId,
      maskId: `${chartId}-mask`,
      gradientId: `${chartId}-gradient`,
    };
  }, []);

  const x = useMemo(
    () =>
      scaleLinear({
        domain: [0, 1],
        range: [0, scaleWidth],
      }),
    [scaleWidth]
  );

  const weightContext = useMemo(
    () => createOrgAssessmentWeightContext(organization),
    [organization]
  );

  const pathToNode = useMemo(() => orgPathRowsForChildWeights(node), [node]);

  const contributions = useMemo(() => {
    const children = node?.children ?? [];

    if (!questionKey) {
      return questionSetContributionFractions(
        children,
        weightContext,
        pathToNode,
        questionSetKey,
        { isScored: isScoredType }
      );
    }

    return childContributionFractions(children, weightContext, pathToNode, {
      getScore: (child) =>
        scoreHelpers.question.getScore(child, questionSetKey, questionKey),
    });
  }, [node, weightContext, pathToNode, questionKey, questionSetKey]);

  const rawDataPoints = useMemo(() => {
    const bins = weightedHistogramBins(contributions, binCount);
    return bins.map(({ xCenter, yValue }) => ({
      x: x(xCenter),
      yValue,
    }));
  }, [contributions, binCount, x]);

  const maxBinSize = max(rawDataPoints, (d) => d.yValue);
  const hasContributions = contributions.some((c) => c.fraction > 0);

  const y = useMemo(() => {
    return scaleLinear({
      domain: [0, maxBinSize || 1],
      range: [scaleHeight, 0],
    });
  }, [maxBinSize, scaleHeight]);

  const areaPoints = rawDataPoints.map(({ x: px, yValue }) => [px, y(yValue)]);

  const gradientPoints = scaleColor.range();
  const stopIncrement = 100 / Math.max(1, gradientPoints.length - 1);

  return (
    <svg width="100%" height="20" viewBox={`0 0 ${width} ${height}`}>
      <g transform={`translate(${margin.left},${margin.top})`}>
        <defs>
          <linearGradient id={gradientId}>
            {gradientPoints.map((stopColor, i) => (
              <stop
                key={`${i}-${stopColor}`}
                stopColor={stopColor}
                offset={`${i * stopIncrement}%`}
              />
            ))}
          </linearGradient>
        </defs>
        <mask id={maskId}>
          <rect
            x="0"
            y="0"
            width={scaleWidth}
            height={scaleHeight}
            fill="black"
          />
          <AreaClosed
            className=""
            data={areaPoints}
            curve={curve}
            yScale={y}
            fill="#fff"
          />
        </mask>
        {hasContributions ? (
          <rect
            x="0"
            y="0"
            height={scaleHeight}
            width={scaleWidth}
            fill={`url(#${gradientId})`}
            mask={`url(#${maskId})`}
          />
        ) : (
          <text
            className="rec-results-dist-unscored"
            textAnchor="middle"
            dominantBaseline="middle"
            x={scaleWidth / 2}
            y={scaleHeight / 2}
          >
            No scored results
          </text>
        )}
        <g>
          <path
            d={`M0,0 l0,${scaleHeight} l${scaleWidth},0 l0,${-scaleHeight}`}
            className="rec-results-dist-bounds"
            fill="none"
          />
        </g>
      </g>
    </svg>
  );
};

export default withParentSize(RecommendationResultsDistribution);
